import React, { useState, useEffect } from 'react';
import { X, Loader, AlertCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import emailjs from '@emailjs/browser';
import { CheckCircle, Calendar, Home } from 'lucide-react';

interface SurveyPopupProps {
  surveyId: string;
  isOpen: boolean;
  onClose: () => void;
  preloadedData?: {
    survey: any;
    questions: Question[];
    choices: Record<string, Choice[]>;
  } | null;
}

interface Question {
  id: string;
  question_text: string;
  question_type: 'multiple_choice' | 'yes_no' | 'contact_form' | 'short_answer' | 'text_only';
  order_number: number;
  is_required: boolean;
  choices?: Choice[];
}

interface Choice {
  id: string;
  choice_text: string;
  order_number: number;
}

interface SurveyResponse {
  [questionId: string]: string | string[];
}

interface OtherResponses {
  [questionId: string]: string;
}

export default function SurveyPopup({ surveyId, isOpen, onClose, preloadedData = null }: SurveyPopupProps) {
  const [survey, setSurvey] = useState<any>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<SurveyResponse>({});
  const [otherResponses, setOtherResponses] = useState<OtherResponses>({});
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (preloadedData) {
        // Use preloaded data if available
        console.log('Using preloaded survey data');
        setSurvey(preloadedData.survey);
        setQuestions(preloadedData.questions.map(question => {
          if (question.question_type === 'multiple_choice' && preloadedData.choices[question.id]) {
            return {
              ...question,
              choices: preloadedData.choices[question.id]
            };
          }
          return question;
        }));
        setLoading(false);
      } else if (surveyId) {
        // Fetch data if not preloaded
        console.log('Fetching survey data on demand');
        fetchSurveyData();
      }
    }
  }, [isOpen, surveyId, preloadedData]);

  const fetchSurveyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch survey details
      const { data: surveyData, error: surveyError } = await supabase
        .from('surveys')
        .select('*')
        .eq('id', surveyId)
        .single();
      
      if (surveyError) {
        throw new Error(`Failed to load survey: ${surveyError.message}`);
      }
      
      setSurvey(surveyData);
      
      // Fetch questions
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .select('*')
        .eq('survey_id', surveyId)
        .order('order_number');
      
      if (questionsError) {
        throw new Error(`Failed to load questions: ${questionsError.message}`);
      }
      
      // Fetch choices for multiple choice questions
      const multipleChoiceQuestions = questionsData?.filter(q => 
        q.question_type === 'multiple_choice'
      ) || [];
      
      const questionsWithChoices = [...questionsData || []];
      
      if (multipleChoiceQuestions.length > 0) {
        for (const question of multipleChoiceQuestions) {
          const { data: choicesData, error: choicesError } = await supabase
            .from('choices')
            .select('*')
            .eq('question_id', question.id)
            .order('order_number');
          
          if (choicesError) {
            console.error(`Failed to load choices for question ${question.id}:`, choicesError);
            continue;
          }
          
          // Find the question in our array and add choices
          const questionIndex = questionsWithChoices.findIndex(q => q.id === question.id);
          if (questionIndex !== -1) {
            questionsWithChoices[questionIndex].choices = choicesData;
          }
        }
      }
      
      setQuestions(questionsWithChoices);
    } catch (err) {
      console.error('Error fetching survey data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load survey');
    } finally {
      setLoading(false);
    }
  };

  const handleNextQuestion = () => {
    // Validate current question if required
    const currentQuestion = questions[currentQuestionIndex];
    
    if (currentQuestion.is_required) {
      // For contact form questions, check required fields individually
      if (currentQuestion.question_type === 'contact_form') {
        const errors: Record<string, string> = {};
        
        if (!contactInfo.name) {
          errors.name = 'Name is required';
        }
        
        if (!contactInfo.email) {
          errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
          errors.email = 'Please enter a valid email address';
        }
        
        if (!contactInfo.company) {
          errors.company = 'Company is required';
        }
        
        // Phone is optional, so no validation needed
        
        if (Object.keys(errors).length > 0) {
          setValidationErrors({
            ...validationErrors,
            ...errors
          });
          return;
        }
        
        // Store contact info as response
        handleResponseChange(currentQuestion.id, JSON.stringify(contactInfo));
      } else {
        // For other question types
        const response = responses[currentQuestion.id];
      
        if (!response || (Array.isArray(response) && response.length === 0)) {
          setValidationErrors({
            ...validationErrors,
            [currentQuestion.id]: 'This question requires an answer'
          });
          return;
        }
      }
    }
    
    // Clear validation error for this question
    const newValidationErrors = { ...validationErrors };
    delete newValidationErrors[currentQuestion.id];
    setValidationErrors(newValidationErrors);
    
    // Move to next question or submit if it's the last question
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Create a synthetic event object to prevent the TypeError
      handleSubmit({ preventDefault: () => {} } as React.FormEvent);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleResponseChange = (questionId: string, value: string | string[]) => {
    setResponses({
      ...responses,
      [questionId]: value
    });
    
    // Clear validation error if any
    if (validationErrors[questionId]) {
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[questionId];
      setValidationErrors(newValidationErrors);
    }
  };

  const handleContactInfoChange = (field: string, value: string) => {
    setContactInfo({
      ...contactInfo,
      [field]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      
      console.log('Submitting survey responses:', responses);
      
      // Generate a UUID for the respondent
      const respondentId = crypto.randomUUID();
      console.log('Generated respondent ID:', respondentId);
      
      // Split the name into first_name and last_name
      const nameParts = contactInfo.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || contactInfo.name.trim(); // Use full name as last name if no space

      console.log('Saving contact information:', {
        first_name: firstName,
        last_name: lastName,
        email: contactInfo.email,
        phone: contactInfo.phone,
        company: contactInfo.company
      });
      
      // Save contact information to CRM
      const { data: contact, error: contactError } = await supabase
        .from('crm_contacts')
        .insert([
          {
            first_name: firstName,
            last_name: lastName,
            email: contactInfo.email,
            phone: contactInfo.phone,
            company: contactInfo.company,
            source: 'AI Audit Survey',
            status: 'prospect'
          }
        ]);

      if (contactError) {
        throw new Error(`Failed to save contact information: ${contactError.message}`);
      }
      
      console.log('Contact saved successfully:', contact);
      
      // Save all responses
      for (const questionId in responses) {
        const response = responses[questionId];
        if (response !== undefined && response !== null) {
          console.log(`Saving response for question ${questionId}:`, response);
          
          const responseData: any = {
            survey_id: surveyId,
            question_id: questionId,
            respondent_id: respondentId,
            view_mode: 'popup'
          };
          
          // Find the question to determine its type
          const question = questions.find(q => q.id === questionId);
          if (!question) continue;
          
          // Handle different question types
          if (question.question_type === 'multiple_choice') {
            if (Array.isArray(response)) {
              // Check if "Other" is selected and we have an other response
              if (response.includes('Other') && otherResponses[questionId]) {
                // Add the "Other" response with the text
                responseData.text_response = `Other: ${otherResponses[questionId]}`;
                
                const { data: savedOtherResponse, error: otherResponseError } = await supabase
                  .from('responses')
                  .insert([{ ...responseData }])
                  .select();
                
                if (otherResponseError) {
                  console.error('Error saving other response:', otherResponseError);
                  throw otherResponseError;
                } else {
                  console.log('Other response saved successfully:', savedOtherResponse);
                }
              }
              
              // For multi-select questions, save each choice as a separate response
              for (const choice of response) {
                // Skip "Other" as we've already handled it separately
                if (choice === 'Other') continue;
                
                responseData.text_response = choice;
                
                const { data: savedResponse, error: responseError } = await supabase
                  .from('responses')
                  .insert([{ ...responseData }])
                  .select();
                
                if (responseError) {
                  console.error('Error saving multi-select response:', responseError);
                  throw responseError;
                } else {
                  console.log('Multi-select response saved successfully:', savedResponse);
                }
              }
              continue; // Skip the regular insert below for multi-select
            } else {
              // Check if this is an "Other" response
              if (response === 'Other' && otherResponses[questionId]) {
                responseData.text_response = `Other: ${otherResponses[questionId]}`;
              } else {
                responseData.text_response = response;
              }
            }
          } else if (question.question_type === 'yes_no') {
            responseData.text_response = response;
          } else if (question.question_type === 'short_answer') {
            responseData.text_response = response;
          } else if (question.question_type === 'contact_form') {
            // For contact form, store the JSON string as text_response
            responseData.text_response = typeof response === 'string' ? response : JSON.stringify(response);
          }
          
          // Save response (except for multi-select which is handled above)
          if (!Array.isArray(response)) {
            const { data: savedResponse, error: responseError } = await supabase
              .from('responses')
              .insert([responseData])
              .select();
            
            if (responseError) {
              console.error('Error saving response:', responseError);
              throw responseError;
            } else {
              console.log('Response saved successfully:', savedResponse);
            }
          }
        }
      }

      // Record survey completion
      const { data: completionData, error: completionError } = await supabase
        .from('survey_completions')
        .insert([{ survey_id: surveyId }]);

      if (completionError) {
        console.error('Error recording survey completion:', completionError);
        throw completionError;
      } else {
        console.log('Survey completion recorded successfully:', completionData);
      }

      // Send email notification using EmailJS
      try {
        const emailjsServiceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
        const emailjsTemplateId = import.meta.env.VITE_EMAILJS_AI_TEMPLATE_ID;
        const emailjsPublicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
        const emailjsSlackEmail = import.meta.env.VITE_EMAILJS_SLACK_EMAIL || 'leads-aaaahdgij3xgdwhoffnd4fd7uu@one80workspace.slack.com';
        
        if (!emailjsServiceId || !emailjsTemplateId || !emailjsPublicKey) {
          console.warn('EmailJS configuration missing. Email notification not sent.');
        } else {
          // Prepare survey responses summary
          const responseSummary = questions.map(question => {
            const response = responses[question.id];
            if (!response) return null;
            
            let displayResponse = '';
            if (question.question_type === 'multiple_choice') {
              // For multi-select questions
              if (Array.isArray(response)) {
                // Check if "Other" is selected
                if (response.includes('Other') && otherResponses[question.id]) {
                  const otherText = `Other: ${otherResponses[question.id]}`;
                  const otherChoices = response.filter(r => r !== 'Other');
                  displayResponse = [...otherChoices, otherText].join(', ');
                } else {
                  displayResponse = response.join(', ');
                }
              } else {
                // Check if this is an "Other" response
                if (response === 'Other' && otherResponses[question.id]) {
                  displayResponse = `Other: ${otherResponses[question.id]}`;
                } else {
                  displayResponse = String(response);
                }
              }
            } else if (question.question_type === 'yes_no') {
              displayResponse = String(response);
            } else if (question.question_type === 'short_answer') {
              displayResponse = String(response);
            } else if (question.question_type === 'contact_form') {
              // Skip contact form in the summary as we're using it separately
              return null;
            }
            
            return `${question.question_text}: ${displayResponse}`;
          }).filter(Boolean).join('\n\n');
          
          // Send the email
          const emailData = {
            to_email: emailjsSlackEmail,
            from_name: contactInfo.name || 'Survey Respondent',
            from_email: contactInfo.email || 'no-email@provided.com',
            subject: 'New AI Audit Survey Submission',
            message: `A new AI Audit survey has been submitted.\n\nContact Information:\nName: ${contactInfo.name}\nEmail: ${contactInfo.email}\nCompany: ${contactInfo.company}\nPhone: ${contactInfo.phone || 'Not provided'}\n\nSurvey Responses:\n\n${responseSummary}`,
            reply_to: contactInfo.email || 'no-reply@one80labs.com'
          };
          
          console.log('Sending email notification with data:', emailData);
          
          const emailResult = await emailjs.send(
            emailjsServiceId,
            emailjsTemplateId,
            emailData,
            emailjsPublicKey
          );
          
          console.log('Email notification sent successfully:', emailResult);
        }
      } catch (emailError) {
        // Don't fail the submission if email sending fails
        console.error('Error sending email notification:', emailError);
      }

      // Close popup and show success message
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset state when the popup is closed
  useEffect(() => {
    // Only reset form data when closing, not the survey data
    if (!isOpen && !preloadedData) {
      setCurrentQuestionIndex(0);
      setResponses({});
      setContactInfo({
        name: '',
        email: '',
        phone: '',
        company: ''
      });
      setOtherResponses({});
      setValidationErrors({});
      setError(null);
      setIsSubmitted(false);
    }
  }, [isOpen, preloadedData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
        
        <div className="p-6">
          {isSubmitted ? (
            <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
              <CheckCircle className="w-16 h-16 text-[#F98B3D] mb-6" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Awesome! Your personalized AI Audit is on the way.</h1>
              <p className="text-lg text-gray-600 mb-8 max-w-md">
                We'll email you 3 custom AI recommendations for your business
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={onCalClick}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] transition-colors duration-200"
                >
                  <Calendar className="h-5 w-5 mr-2" />
                  Free Consultation
                </button>
                <button
                  onClick={onClose}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                >
                  <Home className="h-5 w-5 mr-2" />
                  Back to Lab
                </button>
              </div>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="h-12 w-12 text-[#F98B3D] animate-spin mb-4" />
              <p className="text-gray-600">Loading survey...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{survey?.title || 'AI Audit Survey'}</h2>
                {survey?.description && (
                  <p className="text-gray-600 mt-2">{survey.description}</p>
                )}
                
                {/* Progress indicator */}
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-[#F98B3D] h-2.5 rounded-full" 
                      style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-right text-sm text-gray-500 mt-1">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </p>
                </div>
              </div>
              
              {questions.length > 0 ? (
                <div className="min-h-[300px]">
                  {/* Current question */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {questions[currentQuestionIndex].question_text}
                      {questions[currentQuestionIndex].is_required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </h3>
                    
                    {/* Question content based on type */}
                    {renderQuestionContent(questions[currentQuestionIndex])}
                    
                    {/* Validation error */}
                    {validationErrors[questions[currentQuestionIndex].id] && (
                      <p className="text-sm text-red-600 mt-2">
                        {validationErrors[questions[currentQuestionIndex].id]}
                      </p>
                    )}
                  </div>
                  
                  {/* Navigation buttons */}
                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={handlePreviousQuestion}
                      disabled={currentQuestionIndex === 0}
                      className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium ${
                        currentQuestionIndex === 0
                          ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Previous
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleNextQuestion}
                      disabled={submitting}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
                    >
                      {currentQuestionIndex === questions.length - 1 ? (
                        submitting ? 'Submitting...' : 'Submit'
                      ) : (
                        <>
                          Next
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">No questions found for this survey.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  function onCalClick() {
    // Close the popup and open the Cal widget
    onClose();
    
    // Dispatch a custom event to open the Cal widget
    const event = new CustomEvent('open-cal-widget');
    window.dispatchEvent(event);
  }

  function renderQuestionContent(question: Question) {
    switch (question.question_type) {
      case 'multiple_choice':
        // Check if this is a "Check all that apply" question
        const isMultiSelect = question.question_text.includes("(Check all that apply)");
        
        // Initialize response as array if it's a multi-select question and not already set
        if (isMultiSelect && !responses[question.id]) {
          setResponses(prev => ({
            ...prev,
            [question.id]: []
          }));
        }
        
        return (
          <div className="space-y-3">
            {question.choices?.map((choice) => (
              <label key={choice.id} className="flex items-start">
                <input
                  type={isMultiSelect ? "checkbox" : "radio"}
                  name={question.id}
                  value={choice.choice_text}
                  checked={isMultiSelect 
                    ? Array.isArray(responses[question.id]) && (responses[question.id] as string[]).includes(choice.choice_text)
                    : (responses[question.id] as string) === choice.choice_text
                  }
                  onChange={(e) => {
                    if (isMultiSelect) {
                      // For multi-select, maintain an array of selected values
                      const currentSelections = Array.isArray(responses[question.id])
                        ? [...responses[question.id] as string[]]
                        : [];
                      
                      if (e.target.checked) {
                        // Add to selections
                        handleResponseChange(question.id, [...currentSelections, choice.choice_text]);
                      } else {
                        // Remove from selections
                        handleResponseChange(
                          question.id, 
                          currentSelections.filter(item => item !== choice.choice_text)
                        );
                      }
                    } else {
                      // For single select, just use the value
                      handleResponseChange(question.id, e.target.value);
                    }
                  }}
                  className="mt-1 h-4 w-4 text-[#F98B3D] border-gray-300 focus:ring-[#F98B3D]"
                />
                <span className="ml-3 text-gray-700">{choice.choice_text}</span>
              </label>
            ))}
            
          </div>
        );
        
      case 'yes_no':
        return (
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="radio"
                name={question.id}
                value="Yes"
                checked={(responses[question.id] as string) === 'Yes'}
                onChange={(e) => handleResponseChange(question.id, e.target.value)}
                className="h-4 w-4 text-[#F98B3D] border-gray-300 focus:ring-[#F98B3D]"
              />
              <span className="ml-3 text-gray-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={question.id}
                value="No"
                checked={(responses[question.id] as string) === 'No'}
                onChange={(e) => handleResponseChange(question.id, e.target.value)}
                className="h-4 w-4 text-[#F98B3D] border-gray-300 focus:ring-[#F98B3D]"
              />
              <span className="ml-3 text-gray-700">No</span>
            </label>
          </div>
        );
        
      case 'short_answer':
        return (
          <textarea
            rows={3}
            value={(responses[question.id] as string) || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
            placeholder="Your answer"
          />
        );
        
      case 'contact_form':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name {question.is_required && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={contactInfo.name}
                onChange={(e) => handleContactInfoChange('name', e.target.value)}
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  validationErrors.name ? 'border-red-300' : 'border-gray-300'
                } focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50`}
              />
              {validationErrors.name && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.name}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email {question.is_required && <span className="text-red-500">*</span>}
              </label>
              <input
                type="email"
                value={contactInfo.email}
                onChange={(e) => handleContactInfoChange('email', e.target.value)}
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  validationErrors.email ? 'border-red-300' : 'border-gray-300'
                } focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50`}
              />
              {validationErrors.email && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.email}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Phone
              </label>
              <input
                type="tel"
                value={contactInfo.phone}
                onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Company Website <span className="text-[#F98B3D]">*</span>
              </label>
              <input
                type="text"
                value={contactInfo.company}
                onChange={(e) => handleContactInfoChange('company', e.target.value)}
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  validationErrors.company ? 'border-red-300' : 'border-gray-300'
                } focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50`}
              />
              {validationErrors.company && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.company}</p>
              )}
            </div>
          </div>
        );
        
      case 'text_only':
        // Just display the question text, no input needed
        return null;
        
      default:
        return (
          <p className="text-gray-500 italic">Unsupported question type</p>
        );
    }
  }
}